// src/components/planner/tabs/ReviewTab.tsx
// ABA REVISÃO - Sistema de Revisão Espaçada Inteligente

import React, { useState } from 'react';
import { Plus, CalendarClock, CheckCheck, Edit2, X, Filter, Check, Trash2 } from 'lucide-react';
import ReviewContentModal from '../modals/ReviewContentModal';

const API_BASE_URL = '/api';

interface ReviewTabProps {
    reviewContents: any[];
    onReload: () => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const ReviewTab: React.FC<ReviewTabProps> = ({
    reviewContents,
    onReload,
    onShowToast
}) => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingDate, setEditingDate] = useState<string | null>(null);
    const [newDate, setNewDate] = useState('');
    const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'upcoming' | 'completed'>('all');

    // ========== MARCAR REVISÃO COMO FEITA ==========

    const handleMarkReview = async (contentId: string, reviewDate: string) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/review-contents/${contentId}/mark`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ reviewDate })
            });

            if (response.ok) {
                onShowToast('Revisão marcada!', 'success');
                onReload();
            } else {
                onShowToast('Erro ao marcar revisão', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao marcar revisão', 'error');
        }
    };

    // ========== EDITAR DATA DE REVISÃO ==========

    const handleEditDate = async (contentId: string, reviewIndex: number) => {
        if (!newDate) return;

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/review-contents/${contentId}/update-date`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ reviewIndex, newDate })
            });

            if (response.ok) {
                onShowToast('Data atualizada!', 'success');
                setEditingDate(null);
                setNewDate('');
                onReload();
            } else {
                onShowToast('Erro ao atualizar data', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao atualizar data', 'error');
        }
    };

    // ========== IGNORAR REVISÃO ==========

    const handleIgnoreReview = async (contentId: string, reviewDate: string) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/review-contents/${contentId}/ignore`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ reviewDate })
            });

            if (response.ok) {
                onShowToast('Revisão ignorada', 'info');
                onReload();
            }
        } catch (error) {
            onShowToast('Erro ao ignorar', 'error');
        }
    };

    const handleCompleteContent = async (contentId: string) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/review-contents/${contentId}/complete`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                onShowToast('Conteúdo concluído!', 'success');
                onReload();
            } else {
                onShowToast('Erro ao concluir conteúdo', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao concluir conteúdo', 'error');
        }
    };

    const handleDeleteContent = async (contentId: string) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/review-contents/${contentId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                onShowToast('Conteúdo excluído', 'info');
                onReload();
            } else {
                onShowToast('Erro ao excluir', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao excluir', 'error');
        }
    };

    // ========== HELPERS ==========

    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const isOverdue = (dateString: string): boolean => {
        return new Date(dateString) < new Date();
    };

    const getDaysUntil = (dateString: string): number => {
        const diff = new Date(dateString).getTime() - new Date().getTime();
        return Math.ceil(diff / (1000 * 60 * 60 * 24));
    };

    const getReviewStatus = (content: any): 'pending' | 'completed' | 'upcoming' => {
        if (!content.reviewSchedule || content.reviewSchedule.length === 0) return 'completed';

        const nextReview = content.reviewSchedule.find((r: any) => !r.completed);
        if (!nextReview) return 'completed';

        return isOverdue(nextReview.date) ? 'pending' : 'upcoming';
    };

    // ========== FILTRAR CONTEÚDOS ==========

    const filteredContents = reviewContents.filter(content => {
        if (filterStatus === 'all') return true;
        return getReviewStatus(content) === filterStatus;
    });

    // ========== ESTATÍSTICAS ==========

    const stats = {
        total: reviewContents.length,
        pending: reviewContents.filter(c => getReviewStatus(c) === 'pending').length,
        completed: reviewContents.filter(c => getReviewStatus(c) === 'completed').length,
        upcoming: reviewContents.filter(c => getReviewStatus(c) === 'upcoming').length
    };

    return (
        <div className="plnr-rv-content">
            {/* ========== CABEÇALHO ========== */}
            <div className="plnr-rv-header">
                <div className="plnr-rv-stats-grid">
                    <div className="plnr-rv-stat-card">
                        <span className="plnr-rv-stat-value">{stats.total}</span>
                        <span className="plnr-rv-stat-label">Total</span>
                    </div>
                    <div className="plnr-rv-stat-card pending">
                        <span className="plnr-rv-stat-value">{stats.pending}</span>
                        <span className="plnr-rv-stat-label">Pendentes</span>
                    </div>
                    <div className="plnr-rv-stat-card upcoming">
                        <span className="plnr-rv-stat-value">{stats.upcoming}</span>
                        <span className="plnr-rv-stat-label">Próximas</span>
                    </div>
                    <div className="plnr-rv-stat-card completed">
                        <span className="plnr-rv-stat-value">{stats.completed}</span>
                        <span className="plnr-rv-stat-label">Concluídas</span>
                    </div>
                </div>

                <button
                    className="plnr-rv-btn-add"
                    onClick={() => setShowAddModal(true)}
                >
                    <Plus size={18} />
                    Adicionar Estudo
                </button>
            </div>

            {/* ========== FILTROS ========== */}
            <div className="plnr-rv-filters">
                <Filter size={16} />
                <button
                    className={`plnr-rv-filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
                    onClick={() => setFilterStatus('all')}
                >
                    Todos
                </button>
                <button
                    className={`plnr-rv-filter-btn ${filterStatus === 'pending' ? 'active' : ''}`}
                    onClick={() => setFilterStatus('pending')}
                >
                    Pendentes
                </button>
                <button
                    className={`plnr-rv-filter-btn ${filterStatus === 'upcoming' ? 'active' : ''}`}
                    onClick={() => setFilterStatus('upcoming')}
                >
                    Próximas
                </button>
                <button
                    className={`plnr-rv-filter-btn ${filterStatus === 'completed' ? 'active' : ''}`}
                    onClick={() => setFilterStatus('completed')}
                >
                    Concluídas
                </button>
            </div>

            {/* ========== TABELA DE REVISÕES ========== */}
            <div className="plnr-rv-table-container">
                <table className="plnr-rv-table">
                    <thead>
                        <tr>
                            <th>Conteúdo</th>
                            <th>Data de Estudo</th>
                            <th>Próxima Revisão</th>
                            <th>Status</th>
                            <th>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredContents.length > 0 ? (
                            filteredContents.map(content => {
                                const nextReview = content.reviewSchedule?.find((r: any) => !r.completed);
                                const status = getReviewStatus(content);
                                const daysUntil = nextReview ? getDaysUntil(nextReview.date) : null;

                                return (
                                    <tr key={content._id} className={`plnr-rv-row ${status}`}>
                                        <td className="plnr-rv-content-cell">
                                            <strong>{content.content}</strong>
                                            {content.studyType && (
                                                <span className="plnr-rv-type-badge">{content.studyType}</span>
                                            )}
                                        </td>
                                        <td>{formatDate(content.studyDate)}</td>
                                        <td>
                                            {nextReview ? (
                                                <div className="plnr-rv-date-cell">
                                                    {editingDate === `${content._id}-${nextReview.date}` ? (
                                                        <div className="plnr-rv-date-edit">
                                                            <input
                                                                type="date"
                                                                value={newDate}
                                                                onChange={(e) => setNewDate(e.target.value)}
                                                                className="plnr-rv-date-input"
                                                            />
                                                            <button
                                                                className="plnr-rv-btn-icon success"
                                                                onClick={() => handleEditDate(content._id, content.reviewSchedule.indexOf(nextReview))}
                                                            >
                                                                <Check size={14} />
                                                            </button>
                                                            <button
                                                                className="plnr-rv-btn-icon"
                                                                onClick={() => {
                                                                    setEditingDate(null);
                                                                    setNewDate('');
                                                                }}
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                <span className={isOverdue(nextReview.date) ? 'overdue' : ''}>
                                                                    {formatDate(nextReview.date)}
                                                                </span>
                                                                <button
                                                                    className="plnr-rv-btn-icon"
                                                                    onClick={() => {
                                                                        setEditingDate(`${content._id}-${nextReview.date}`);
                                                                        setNewDate(nextReview.date.split('T')[0]);
                                                                    }}
                                                                >
                                                                    <Edit2 size={14} />
                                                                </button>
                                                            </div>
                                                            {daysUntil !== null && (
                                                                <span className="plnr-rv-days-until">
                                                                    {daysUntil === 0 ? 'Hoje' :
                                                                        daysUntil === 1 ? 'Amanhã' :
                                                                            daysUntil > 0 ? `Em ${daysUntil}d` :
                                                                                `${Math.abs(daysUntil)}d atrás`}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="plnr-rv-completed-label">Concluído</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`plnr-rv-status-badge ${status}`}>
                                                {status === 'pending' ? 'Pendente' :
                                                    status === 'upcoming' ? 'Próxima' : 'Concluída'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="plnr-rv-actions">
                                                {nextReview && (
                                                    <>
                                                        <button
                                                            className="plnr-rv-btn-action success"
                                                            onClick={() => handleMarkReview(content._id, nextReview.date)}
                                                            title="Programar próxima revisão"
                                                        >
                                                            <CalendarClock size={16} />
                                                        </button>
                                                        <button
                                                            className="plnr-rv-btn-action secondary"
                                                            onClick={() => handleIgnoreReview(content._id, nextReview.date)}
                                                            title="Ignorar esta revisão"
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </>
                                                )}
                                                {status !== 'completed' && (
                                                    <button
                                                        className="plnr-rv-btn-action complete"
                                                        onClick={() => handleCompleteContent(content._id)}
                                                        title="Concluir conteúdo"
                                                    >
                                                        <CheckCheck size={16} />
                                                    </button>
                                                )}

                                                {status === 'completed' && (
                                                    <button
                                                        className="plnr-rv-btn-action delete"
                                                        onClick={() => handleDeleteContent(content._id)}
                                                        title="Excluir conteúdo"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={5} className="plnr-rv-empty">
                                    Nenhum conteúdo para revisão
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* ========== INFO BOX ========== */}
            <div className="plnr-rv-info-box">
                <h4>Como funciona a Revisão Espaçada?</h4>
                <p>
                    Sugerimos revisões automaticamente baseadas no que você estudou.
                    As datas são apenas sugestões — você pode adiar, ignorar ou revisar quando quiser.
                    O importante é que você esteja no controle!
                </p>
            </div>

            {/* MODAL */}
            {showAddModal && (
                <ReviewContentModal
                    onClose={() => setShowAddModal(false)}
                    onSave={() => {
                        setShowAddModal(false);
                        onReload();
                    }}
                    onShowToast={onShowToast}
                />
            )}
        </div>
    );
};

export default ReviewTab;